import { PublicClientApplication } from "@azure/msal-browser";
import { DefaultApi as Api } from "@coeff/api";
import { DefaultApi as AdminExtApi } from "@coeff/api-admin-ext";
import { DefaultApi as CoeffAdminApi } from "@coeff/api-coeff-admin";
import * as Sentry from "@sentry/react";
import axios, { InternalAxiosRequestConfig } from "axios";
import { Store } from "redux";

import { config } from "../config";
import { AppState } from "../store";
import {
  SSOAuthFailedError,
  isAxiosErrorWithSSOAuthFailedMessage,
  publicClientAppScopes,
} from "../utils";

export const setMicrosoftOAuthTokenHeader = async (
  publicClientApp: PublicClientApplication,
  config: InternalAxiosRequestConfig<any>
) => {
  try {
    const tokenResponse = await publicClientApp.acquireTokenSilent({
      scopes: publicClientAppScopes,
    });

    if (tokenResponse?.accessToken) {
      config.headers["MICROSOFT-OAUTH-TOKEN"] = tokenResponse.accessToken;
    }
  } catch (error: unknown) {
    if (
      typeof error === "object" &&
      error !== null &&
      "name" in error &&
      error.name === "BrowserAuthError"
    ) {
      // Ignore errors. We're either not logged in or we're logged in with Google.
    } else {
      console.error(error);
      Sentry.captureException(error);
    }
  }
};

export const initApiClients = (params: {
  publicClientApp: PublicClientApplication;
  store: Store<AppState>;
  onUnauthorizedError: (error: unknown) => void;
}) => {
  const { store, publicClientApp, onUnauthorizedError } = params;

  const axiosInstance = axios.create({
    headers: {
      "Content-Type": "application/json",
      "X-Addon-Platform": "webapp",
    },
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use(async axiosConfig => {
    const storeState = store.getState();

    await setMicrosoftOAuthTokenHeader(publicClientApp, axiosConfig);

    const params = new URLSearchParams(window.location.search);

    const oauthTokenFromUrl = params.get("GOOGLE-OAUTH-TOKEN");
    const openIdTokenFromUrl = params.get("GOOGLE-OPENID-TOKEN");
    const upgradeTokenFromUrl = params.get("ut");
    const userIdFromUrl = params.get("__user_id__");

    if (openIdTokenFromUrl ?? storeState.app.openIdToken) {
      // Tokens in the URL take precedence over tokens in the store
      axiosConfig.headers["GOOGLE-OPENID-TOKEN"] = openIdTokenFromUrl ?? storeState.app.openIdToken;
    }

    if (oauthTokenFromUrl ?? storeState.app.oauthToken) {
      // Tokens in the URL take precedence over tokens in the store
      axiosConfig.headers["GOOGLE-OAUTH-TOKEN"] = oauthTokenFromUrl ?? storeState.app.oauthToken;
    }

    if (upgradeTokenFromUrl ?? storeState.app.upgradeToken) {
      axiosConfig.headers["ut"] = upgradeTokenFromUrl ?? storeState.app.upgradeToken;
    }

    if (storeState.app.coeffAuthToken) {
      axiosConfig.headers["COEFF-AUTH-TOKEN"] = storeState.app.coeffAuthToken;
    }

    // Tell the backend when a request originates from a web dashboard page so a user who
    // authorizes Microsoft for OneDrive access is created as a web dashboard user, not stamped
    // as an Excel add-in installer.
    const webDashboardMatch = window.location.pathname.match(/^\/dashboards\/([a-zA-Z0-9_-]+)$/);
    if (webDashboardMatch) {
      axiosConfig.headers["X-Web-Dashboard-Id"] = webDashboardMatch[1];
    }

    // Append __user_id__ to request URL ONLY on localhost (for debug mode)
    const isLocalhost =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    if (userIdFromUrl && isLocalhost && axiosConfig.url) {
      const url = new URL(axiosConfig.url, axiosConfig.baseURL || config.BASE_URL);
      url.searchParams.set("__user_id__", userIdFromUrl);
      axiosConfig.url = url.pathname + url.search;
    }

    return axiosConfig;
  });

  axiosInstance.interceptors.response.use(
    config => config,
    response => {
      if (isAxiosErrorWithSSOAuthFailedMessage(response)) {
        onUnauthorizedError(new SSOAuthFailedError());
      }

      return Promise.reject(response);
    }
  );

  return {
    apiClient: new Api(undefined, config.BASE_URL, axiosInstance),
    adminExtApiClient: new AdminExtApi(undefined, config.BASE_URL, axiosInstance),
    coeffAdminApiClient: new CoeffAdminApi(undefined, config.BASE_URL, axiosInstance),
  };
};
